import React, { useMemo, useState } from 'react';
import { formatDecimal as fmt } from '../helpers/paymentsHelper';

const ModalTable = ({ data }) => {
  const getByPath = (obj, path) =>
    path.split('.').reduce((acc, key) => (acc?.[key] ?? undefined), obj);

  const cmp = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumbers = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumbers) return aNum - bNum;

    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => dir * cmp(getByPath(a, sortConfig.key), getByPath(b, sortConfig.key)));
  }, [data, sortConfig]);

  const filteredData = sortedData.filter((item) =>
    Object.values(item).some((value) => value.toString().toLowerCase())
  );

  return (
    <div className="w-full h-full">
      <table className="border-collapse w-full h-10/12 overflow-y-auto">
        <thead>
          <tr>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('account_name')}>
              Drivers{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'account_name' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'account_name' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.trips')}>
              Total Trips{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.trips' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.trips' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.bonus')}>
              Bon{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.bonus' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.bonus' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.deduction')}>
              Ded{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.deduction' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.deduction' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('performance_expense')}>
              Performance{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'performance_expense' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'performance_expense' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.fuel')}>
              Fuel{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.fuel' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.fuel' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.km_overuse')}>
              KMs{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.km_overuse' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.km_overuse' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.bolt')}>
              Bolt{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.bolt' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.bolt' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.uber')}>
              Uber{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.uber' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.uber' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
            <th
              className="border-b p-2 cursor-pointer text-left"
              onClick={() => handleSort('snapshot.other')}>
              Other{' '}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="32"
                className="inline-block ml-1">
                <path
                  d="M12 5l-5 5h10l-5-5z"
                  fill={
                    sortConfig.key === 'snapshot.other' && sortConfig.direction === 'asc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />

                <path
                  d="M12 19l5-5H7l5 5z"
                  fill={
                    sortConfig.key === 'snapshot.other' && sortConfig.direction === 'desc'
                      ? '#745D96'
                      : '#9C9C9C'
                  }
                />
              </svg>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <React.Fragment key={item.account_id}>
              <tr className="border-t border-b p-2 cursor-pointer">
                <td className="border-t border-b p-2 text-left">{item.account_name}</td>
                <td className="border-t border-b p-2 text-left">
                  {item.snapshot.trips}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color:
                      item.snapshot.bonus >= 0 ? 'green' : 'red',
                  }}>
                  {fmt(item.snapshot.bonus)}
                </td>
                <td
                  className="border-t border-b p-2 text-left" style={{color: "red"}}>
                  {fmt(item.snapshot.deduction)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color: item.snapshot.performance_expense >= 0 ? 'red' : 'green',
                  }}>
                  {fmt(item.snapshot.performance_expense)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{ color: item.snapshot.fuel >= 0 ? 'red' : 'green' }}>
                  {fmt(item.snapshot.fuel)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color: item.snapshot.km_overuse >= 0 ? 'red' : 'green',
                  }}>
                  {fmt(item.snapshot.km_overuse)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color: item.snapshot.other >= 0 ? 'green' : 'red',
                  }}>
                  {fmt(item.snapshot.bolt)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color: item.snapshot.other >= 0 ? 'green' : 'red',
                  }}>
                  {fmt(item.snapshot.uber)}
                </td>
                <td
                  className="border-t border-b p-2 text-left"
                  style={{
                    color: item.snapshot.other >= 0 ? 'green' : 'red',
                  }}>
                  {fmt(item.snapshot.other)}
                </td>
                {/* <td className="border-t border-b p-2 space-x-1">
                  <button className="p-2 bg-purple-600 rounded hover:bg-purple-200">
                    <svg
                      width="18"
                      height="16"
                      viewBox="0 0 18 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M12.5389 2.59738L15.3481 5.41668C15.4665 5.53546 15.4665 5.72924 15.3481 5.84802L8.54613 12.6744L5.65589 12.9963C5.2697 13.04 4.94268 12.7119 4.98628 12.3243L5.30707 9.42372L12.1091 2.59738C12.2274 2.47861 12.4205 2.47861 12.5389 2.59738ZM17.5843 1.88162L16.0645 0.35632C15.5911 -0.118773 14.8218 -0.118773 14.3453 0.35632L13.2428 1.46279C13.1244 1.58156 13.1244 1.77535 13.2428 1.89412L16.052 4.71342C16.1704 4.83219 16.3635 4.83219 16.4818 4.71342L17.5843 3.60695C18.0577 3.12874 18.0577 2.35671 17.5843 1.88162ZM11.9596 10.8177V13.9996H1.99327V3.99766H9.15034C9.25 3.99766 9.34343 3.95702 9.41507 3.88826L10.6609 2.63802C10.8976 2.40047 10.7294 1.99727 10.3961 1.99727H1.49495C0.669613 1.99727 0 2.66927 0 3.49756V14.4997C0 15.328 0.669613 16 1.49495 16H12.4579C13.2832 16 13.9529 15.328 13.9529 14.4997V9.56749C13.9529 9.23305 13.5511 9.0674 13.3144 9.30182L12.0686 10.5521C12.0001 10.624 11.9596 10.7177 11.9596 10.8177Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </td> */}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ModalTable;
